import { create } from 'zustand'

import { toast } from '@/shared/lib/toast'

import * as dcrApi from '../api/dcr.api'

const initialState = {
  dcrs: [],
  meta: {
    page: 1,
    limit: 5,
    total: 0,
    totalPages: 0,
  },
  isLoading: true,
  isSubmitting: false,
  error: null,
  filters: {
    search: '',
  },
}

export const useDCRStore = create((set, get) => ({
  ...initialState,

  clearError: () => set({ error: null }),

  setFilters: (partial) =>
    set((state) => ({
      filters: {
        ...state.filters,
        ...partial,
      },
    })),

  fetchDCRs: async (params = {}) => {
    const filters = {
      ...get().filters,
      ...params,
    }

    set({
      isLoading: true,
      error: null,
      filters,
    })

    try {
      const query = {
        page: filters.page || 1,
        limit: filters.limit || 5,
        search: filters.search || undefined,
        period: filters.period || undefined,
        salesPerson: filters.salesPerson || undefined,
        date: filters.date || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      }

      const result = await dcrApi.getDCRs(query)

      set({
        dcrs: result.data ?? [],
        meta: result.meta ?? {
          page: query.page,
          limit: query.limit,
          total: 0,
          totalPages: 0,
        },
        isLoading: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to fetch DCRs'

      set({
        isLoading: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  createDCR: async (payload) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await dcrApi.createDCR(payload)

      toast.success(result.message || 'DCR created successfully')

      await get().fetchDCRs()

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to create DCR'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  updateDCR: async (id, payload) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await dcrApi.updateDCR(id, payload)

      toast.success(result.message || 'DCR updated successfully')

      await get().fetchDCRs()

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to update DCR'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },

  deleteDCR: async (id) => {
    if (get().isSubmitting) return null

    set({
      isSubmitting: true,
      error: null,
    })

    try {
      const result = await dcrApi.deleteDCR(id)

      toast.success(result.message || 'DCR deleted successfully')

      await get().fetchDCRs()

      set({
        isSubmitting: false,
        error: null,
      })

      return result
    } catch (error) {
      const message = error.message || 'Failed to delete DCR'

      set({
        isSubmitting: false,
        error: message,
      })

      toast.error(message)
      throw error
    }
  },
}))
